import { requestPartnerAPI } from "../../api/server";
import { AuthContext } from "../../context/auth-context";
import Heading from "../UI/Heading";
import { useState, useContext } from "react";
import uploadFile from "../../util/uploadFile";
import LoadingSpinner from "../UI/LoadingSpinner";
import DetailPair from "../UI/DetailPair";
import LinkPair from "../UI/LinkPair";
import { toTitleCase } from "../../util/toTitleCase";
import { MdHelp } from "react-icons/md";
import { UtilityContext } from "../../context/util-context";
import { constructDateObject } from "../../util/format-date";

export default function AddVehicle() {
  const { user } = useContext(AuthContext);
  const { navigate, setIssue } = useContext(UtilityContext);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [item, setItem] = useState();

  // ==================================== VIN INPUT HANDLER
  /** This function is used to decode the VIN using POSH's API
   * and set the resulting vehicle information in the form */
  const vinInputHandler = async (vin) => {
    try {
      // Check if VIN is complete
      if (vin.length !== 17) return;
      setLoading(true);
      // Send request to decode VIN
      const response = await requestPartnerAPI("decode-vin", { vin: vin });
      // Extract Results
      const vehicleInfo = response.vehicleInfo;

      // Set Results
      document.getElementById("make").value = vehicleInfo.make;
      document.getElementById("model").value = toTitleCase(vehicleInfo.model);
      document.getElementById("year").value = vehicleInfo.year;

      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  // ==================================== SUBMIT HANDLER
  const submitHandler = async () => {
    try {
      setLoading(true);

      // Get Field Values
      const payload = {
        vin: document.getElementById("vin").value,
        make: document.getElementById("make").value,
        model: document.getElementById("model").value,
        year: document.getElementById("year").value,
        license: document.getElementById("license").value,
        licenseState: document.getElementById("licenseState").value,
        institution: document.getElementById("institution").value,
        financeAmount: document.getElementById("financeAmount").value,
        monthlyPayment: document.getElementById("monthlyPayment").value,
        loanTerm: document.getElementById("loanTerm").value,
      };
      const dueDate = document.getElementById("dueDate").value;
      if (dueDate) {
        const date = constructDateObject(dueDate, "13:00:00");
        payload.dueDate = date.toISOString();
      }

      // Get Files
      const files = {
        title: document.getElementById("title")?.files[0],
        registeration: document.getElementById("registration")?.files[0],
        insurance: document.getElementById("insurance")?.files[0],
        billOfSale: document.getElementById("billOfSale")?.files[0],
        vehicleInspectionReport: document.getElementById(
          "vehicleInspectionReport"
        )?.files[0],
        loanDocument: document.getElementById("loanDocument")?.files[0],
      };

      // Upload Files
      for (const key in files) {
        if (!files[key]) continue;
        const path = `partners/${user.uid}/${
          payload.vin
        }/${key}-${new Date().getTime()}`;
        setItem(key);
        const url = await uploadFile(path, files[key], setUploading);
        payload[key] = url;
      }

      // Send Request
      await requestPartnerAPI("add-application", payload);
      navigate(`/vehicles/poa?vin=${payload.vin}`);
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  return (
    <div className="grid content-start gap-6 bg">
      <Heading
        title="Add Vehicle"
        description="Please begin with entering the complete VIN, which will fill out all mandatory fields. You may leave out other information and update it later. DISCLAIMER: Adding a vehicle does not guarantee that it will be onboarded onto the POSH platform."
        isBackArrow={true}
      >
        <div
          onClick={() => setIssue(true)}
          className="flex items-center space-x-2 text-sm btn-secondary"
        >
          <p>Report Issue</p>
          <MdHelp className="text-xl text-stone-200" />
        </div>
      </Heading>

      <div className="grid-cols-4 gap-4 info">
        <DetailPair
          label="VIN"
          edit={true}
          inputOptions={{
            id: "vin",
            maxLength: 17,
            onChange: (e) => vinInputHandler(e.target.value),
          }}
        />
        <DetailPair
          label="Make"
          edit={true}
          inputOptions={{
            id: "make",
            type: "text",
            disabled: true,
          }}
        />
        <DetailPair
          label="Model"
          edit={true}
          inputOptions={{
            id: "model",
            type: "text",
            disabled: true,
          }}
        />
        <DetailPair
          label="Year"
          edit={true}
          inputOptions={{
            id: "year",
            type: "number",
            disabled: true,
          }}
        />
        <DetailPair
          label="License"
          edit={true}
          inputOptions={{
            id: "license",
            type: "text",
          }}
        />
        <DetailPair
          label="License State"
          edit={true}
          inputOptions={{
            id: "licenseState",
            type: "text",
            maxLength: 2,
          }}
        />
        <DetailPair
          label="Financing Institution"
          edit={true}
          inputOptions={{
            id: "institution",
            type: "text",
          }}
        />
        <DetailPair
          label="Amount Financed"
          edit={true}
          inputOptions={{
            id: "financeAmount",
            type: "text",
          }}
        />
        <DetailPair
          label="Monthly Installment"
          edit={true}
          inputOptions={{
            id: "monthlyPayment",
            type: "text",
          }}
        />
        <DetailPair
          label="Term (Months)"
          edit={true}
          inputOptions={{
            id: "loanTerm",
            type: "number",
          }}
        />
        <DetailPair
          label="Due Date"
          edit={true}
          inputOptions={{
            id: "dueDate",
            type: "date",
          }}
        />
      </div>
      <div className="grid-cols-4 gap-4 info">
        <LinkPair
          label="Loan Documents"
          edit={true}
          inputOptions={{
            id: "loanDocument",
            type: "file",
          }}
        />
        <LinkPair
          label="Registration"
          edit={true}
          inputOptions={{
            id: "registration",
            type: "file",
          }}
        />
        <LinkPair
          label="Title"
          edit={true}
          inputOptions={{
            id: "title",
            type: "file",
          }}
        />
        <LinkPair
          label="Inspection Report"
          edit={true}
          inputOptions={{
            id: "vehicleInspectionReport",
            type: "file",
          }}
        />
        <LinkPair
          label="Bill of Sale"
          edit={true}
          inputOptions={{
            id: "billOfSale",
            type: "file",
          }}
        />
        <LinkPair
          label="Insurance"
          edit={true}
          inputOptions={{
            id: "insurance",
            type: "file",
          }}
        />
      </div>

      <div className="grid-cols-4 mt-6 info">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div onClick={submitHandler} className="btn-primary">
            Submit
          </div>
        )}
      </div>
    </div>
  );
}
